import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  TextField,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import api from '../api/axios';

const STATUS_COLORS = {
  open: 'error',
  in_progress: 'warning',
  resolved: 'success',
  wont_fix: 'default'
};

const SOURCE_LABELS = {
  feature_gap: 'Feature Gap',
  backtest_exhausted: 'Backtest Exhausted',
  diagnose: 'Diagnosis',
  runtime_error: 'Runtime Error'
};

/**
 * Admin Escalations Page
 * Queue of issues the agent system could not fix by editing flow config.
 * Each escalation includes a Cursor-ready prompt the admin can copy into the
 * IDE to implement the platform fix.
 */
const AdminEscalationsPage = () => {
  const [escalations, setEscalations] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [snackbar, setSnackbar] = useState(null);

  const loadEscalations = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const response = await api.get('/admin-escalations', { params });
      setEscalations(response.data.escalations || []);
      setCounts(response.data.counts || {});
    } catch (error) {
      console.error('Error loading escalations:', error);
      setSnackbar({ severity: 'error', message: 'Failed to load escalations' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadEscalations();
  }, [loadEscalations]);

  const copyCursorPrompt = async (escalation) => {
    try {
      await navigator.clipboard.writeText(escalation.cursorPrompt || '');
      setSnackbar({ severity: 'success', message: 'Cursor prompt copied to clipboard - paste it into Cursor to implement the fix' });
    } catch (error) {
      setSnackbar({ severity: 'error', message: 'Could not copy to clipboard' });
    }
  };

  const updateStatus = async (escalation, status) => {
    try {
      await api.patch(`/admin-escalations/${escalation.escalationId}`, {
        status,
        ...(notes ? { resolutionNotes: notes } : {})
      });
      setSnackbar({ severity: 'success', message: `Escalation marked as ${status.replace('_', ' ')}` });
      setSelected(null);
      setNotes('');
      loadEscalations();
    } catch (error) {
      setSnackbar({ severity: 'error', message: 'Failed to update escalation' });
    }
  };

  const openDetail = (escalation) => {
    setSelected(escalation);
    setNotes(escalation.resolutionNotes || '');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Admin Escalations
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadEscalations}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Issues the AI agents could not fix by editing flow configuration. Each one
        includes a ready-to-paste Cursor prompt for implementing the platform fix.
      </Typography>

      <Tabs value={statusFilter} onChange={(e, v) => setStatusFilter(v)} sx={{ mb: 2 }}>
        <Tab value="open" label={`Open${counts.open ? ` (${counts.open})` : ''}`} />
        <Tab value="in_progress" label={`In Progress${counts.in_progress ? ` (${counts.in_progress})` : ''}`} />
        <Tab value="resolved" label={`Resolved${counts.resolved ? ` (${counts.resolved})` : ''}`} />
        <Tab value="wont_fix" label="Won't Fix" />
        <Tab value="all" label="All" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : escalations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No escalations in this view.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Created</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {escalations.map((esc) => (
                <TableRow key={esc.escalationId} hover>
                  <TableCell>{new Date(esc.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{esc.projectId?.name || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={SOURCE_LABELS[esc.source] || esc.source} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={esc.title}>
                      {esc.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={esc.severity}
                      color={esc.severity === 'high' ? 'error' : esc.severity === 'medium' ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={esc.status.replace('_', ' ')} color={STATUS_COLORS[esc.status]} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Copy Cursor Prompt">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => copyCursorPrompt(esc)}
                          disabled={!esc.cursorPrompt}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => openDetail(esc)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              {selected.title}
              <Typography variant="caption" display="block" color="text.secondary">
                {selected.escalationId} · {SOURCE_LABELS[selected.source] || selected.source} ·{' '}
                {new Date(selected.createdAt).toLocaleString()}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {selected.summary && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selected.summary}
                </Typography>
              )}

              {selected.affectedFilesGuess?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Suspected Files</Typography>
                  {selected.affectedFilesGuess.map((f, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>
                      {f}
                    </Typography>
                  ))}
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Cursor Prompt
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#f8f8f8',
                  maxHeight: 320,
                  overflowY: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: 12
                }}
              >
                {selected.cursorPrompt || 'No Cursor prompt was generated for this escalation.'}
              </Paper>

              <TextField
                label="Resolution notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 2 }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => copyCursorPrompt(selected)}
                disabled={!selected.cursorPrompt}
                variant="contained"
              >
                Copy Cursor Prompt
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              {selected.status !== 'in_progress' && (
                <Button onClick={() => updateStatus(selected, 'in_progress')}>In Progress</Button>
              )}
              {selected.status !== 'resolved' && (
                <Button color="success" onClick={() => updateStatus(selected, 'resolved')}>
                  Resolve
                </Button>
              )}
              {selected.status !== 'wont_fix' && (
                <Button color="inherit" onClick={() => updateStatus(selected, 'wont_fix')}>
                  Won't Fix
                </Button>
              )}
              <Button onClick={() => setSelected(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : <span />}
      </Snackbar>
    </Container>
  );
};

export default AdminEscalationsPage;
